import { articlePath, type Article } from "@/data/articles";
import { calculatorPages, type CalculatorPageKey } from "@/data/calculatorPages";

export const SITE_ORIGIN = "https://www.foodbowlscience.com";
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-foodbowlscience.png`;
// Retained: site-level fallback date. No longer used for per-article JSON-LD dates.
export const ARTICLE_METADATA_DATE = "2026-06-06";

export type SeoPage =
  | "home"
  | "method"
  | "label-guide"
  | "articles"
  | "safety"
  | "privacy"
  | "dog-calorie-calculator"
  | "cat-calorie-calculator";

export const seoContent: Record<SeoPage, { title: string; description: string; canonical: string }> = {
  home: {
    title: "FoodBowlScience | Scientific Pet Feeding Calculator",
    description:
      "Calculate how much to feed your dog or cat by weight, age, and goal from veterinary formulas and your food's calorie density. Daily calories, grams, and cups.",
    canonical: `${SITE_ORIGIN}/`,
  },
  method: {
    title: "How the Calculator Works | FoodBowlScience",
    description:
      "FoodBowlScience uses the standard resting energy requirement formula and your food's actual calorie density to estimate daily feeding amounts for dogs and cats.",
    canonical: `${SITE_ORIGIN}/method/`,
  },
  "label-guide": {
    // Description used to describe only calorie density, while the queries reaching this page
    // are about pet food labelling generally. Broadened 2026-08-02 to match the demand it
    // already has (232 impressions, position 43.7) without overpromising.
    title: "How to Read a Pet Food Label | FoodBowlScience",
    description:
      "How to read a dog or cat food label: where to find kcal/kg on EU and UK labels, the US Calorie Content statement, and what life-stage wording means.",
    canonical: `${SITE_ORIGIN}/label-guide/`,
  },
  articles: {
    title: "Pet Nutrition Articles | FoodBowlScience",
    description:
      "Evidence-based articles on overfeeding, underfeeding, food labels, healthy weight, and pet food quality, using verified veterinary and scientific references.",
    canonical: `${SITE_ORIGIN}/articles/`,
  },
  safety: {
    title: "Safety and Limitations | FoodBowlScience",
    description:
      "FoodBowlScience provides estimates, not veterinary advice. Learn when to consult a vet and how to use calculator output responsibly.",
    canonical: `${SITE_ORIGIN}/safety/`,
  },
  "dog-calorie-calculator": {
    title: "Dog Calorie Calculator: How Much to Feed Daily | FoodBowlScience",
    description:
      "Free dog calorie calculator: daily calories from your dog's weight and life stage, converted to grams or cups for the exact food on your label.",
    canonical: `${SITE_ORIGIN}/dog-calorie-calculator/`,
  },
  "cat-calorie-calculator": {
    title: "Cat Calorie Calculator: How Much to Feed Daily | FoodBowlScience",
    description:
      "Free cat calorie calculator: daily calories from your cat's weight and life stage, converted to grams or cups for the dry or wet food you feed.",
    canonical: `${SITE_ORIGIN}/cat-calorie-calculator/`,
  },
  privacy: {
    title: "Privacy Notice | FoodBowlScience",
    description: "FoodBowlScience does not store calculator inputs or pet profiles. Read the full privacy notice.",
    canonical: `${SITE_ORIGIN}/privacy/`,
  },
};

export const homeJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebApplication",
  name: "FoodBowlScience",
  url: `${SITE_ORIGIN}/`,
  description:
    "A pet feeding calculator for dogs and cats using veterinary metabolic formulas and label-accurate calorie density.",
  applicationCategory: "HealthApplication",
  operatingSystem: "All",
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
};

type CalculatorJsonLd = {
  "@context": string;
  "@graph": [
    Omit<typeof homeJsonLd, "@context">,
    {
      "@type": "FAQPage";
      mainEntity: { "@type": "Question"; name: string; acceptedAnswer: { "@type": "Answer"; text: string } }[];
    },
  ];
};

export function calculatorJsonLd(page: CalculatorPageKey): CalculatorJsonLd {
  const c = seoContent[page];
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebApplication",
        name: c.title.replace(/ \| FoodBowlScience$/, ""),
        url: c.canonical,
        description: c.description,
        applicationCategory: "HealthApplication",
        operatingSystem: "All",
        offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      },
      {
        "@type": "FAQPage",
        mainEntity: calculatorPages[page].faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: { "@type": "Answer", text: faq.a },
        })),
      },
    ],
  };
}

export type ResolvedSeo = {
  title: string;
  description: string;
  canonical: string;
  ogType: "website" | "article";
  ogImage: string;
  ogImageAlt: string;
};

export function resolveSeo(page: SeoPage, article?: Article): ResolvedSeo {
  if (article) {
    return {
      title: `${article.seoTitle ?? article.title} | FoodBowlScience`,
      description: article.metaDescription,
      canonical: `${SITE_ORIGIN}${articlePath(article.slug)}`,
      ogType: "article",
      ogImage: article.image ? `${SITE_ORIGIN}${article.image.src}` : DEFAULT_OG_IMAGE,
      ogImageAlt: article.image?.alt ?? "FoodBowlScience scientific pet feeding calculator",
    };
  }
  const c = seoContent[page];
  return {
    title: c.title,
    description: c.description,
    canonical: c.canonical,
    ogType: "website",
    ogImage: DEFAULT_OG_IMAGE,
    ogImageAlt: "FoodBowlScience scientific pet feeding calculator",
  };
}

export function articleJsonLd(article: Article): Record<string, unknown> {
  const canonical = `${SITE_ORIGIN}${articlePath(article.slug)}`;
  const ogImage = article.image ? `${SITE_ORIGIN}${article.image.src}` : DEFAULT_OG_IMAGE;
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.metaDescription,
    image: [ogImage],
    mainEntityOfPage: canonical,
    author: { "@type": "Organization", name: "FoodBowlScience" },
    publisher: {
      "@type": "Organization",
      name: "FoodBowlScience",
      logo: { "@type": "ImageObject", url: `${SITE_ORIGIN}/favicon.svg` },
    },
    // Was hardcoded to ARTICLE_METADATA_DATE (2026-06-06) for every article, so structured
    // data told search engines that even an article published yesterday was four months old.
    // Fixed 2026-08-02 when Article gained a real `published` field.
    datePublished: article.published,
    dateModified: article.updated ?? article.published,
  };
}
